import React, { lazy, Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';

import HowItWorks from './HowItWorks';
import Hero from './Hero';
import Banner from './Banner';
import TradesCara from './TradesCara';
import Accordian from './Accordian';
import Footer from './Footer';
import TradeBanner from './TradeBanner';
import { Helmet } from 'react-helmet-async';

const InstagramFeedSection = lazy(() => import('./InstagramFeedSection'));

const ORIGIN = 'https://tradesutrust.co.uk';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding: 28px 0 24px;
  min-height: 100vh;
  background:
    radial-gradient(1200px 420px at 50% -5%, rgba(30, 102, 255, 0.08), transparent 55%),
    #f7f9fc;

  & > * + * {
    margin-top: 2.25rem;
  }

  @media (max-width: 768px) {
    padding: 52px 0 16px;

    & > * + * {
      margin-top: 1.5rem;
    }
  }
`;

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.7rem, 4.5vw, 2.3rem);
  text-align: center;
  color: #0b3d73;
  margin: 0;
  letter-spacing: -0.02em;
  text-wrap: balance;
`;

const SeoContentSection = styled.section`
  width: 95%;
  max-width: 1100px;
  padding: 24px 20px;
  background: #ffffff;
  border: 1px solid #d7e6f8;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
`;

const SeoHeading = styled.h2`
  margin: 0 0 14px;
  color: #0b3d73;
  font-size: clamp(1.4rem, 3vw, 1.95rem);
  line-height: 1.2;
`;

const SeoSubHeading = styled.h3`
  margin: 20px 0 10px;
  color: #1b4f82;
  font-size: clamp(1.08rem, 2.4vw, 1.3rem);
`;

const SeoParagraph = styled.p`
  margin: 0 0 12px;
  color: #22384e;
  line-height: 1.7;
  font-size: 1rem;
`;

const SeoList = styled.ul`
  margin: 0 0 8px;
  padding-left: 20px;
  color: #22384e;
  line-height: 1.7;
`;

const DeferredBlock = styled.div`
  width: 95%;
  max-width: 1100px;
`;

function HomePage({ formState, setFormState }) {
  const [showInstagram, setShowInstagram] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowInstagram(true), 1800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <PageContainer>
      <Helmet>
        <title>TradesUTrust | Find Trusted Tradespeople Across the UK</title>
        <meta
          name="description"
          content="Find verified electricians, plumbers, carpenters and more across the UK. Post a job free and get quotes fast with TradesUTrust."
        />
        <link rel="canonical" href={`${ORIGIN}/`} />
        <link rel="alternate" hrefLang="en-gb" href={`${ORIGIN}/`} />
        <link rel="alternate" hrefLang="x-default" href={`${ORIGIN}/`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TradesUTrust" />
        <meta property="og:title" content="TradesUTrust - Find Trusted Tradespeople Near You" />
        <meta property="og:description" content="Post a job free, compare quotes, and hire trusted UK tradespeople with confidence." />
        <meta property="og:url" content={`${ORIGIN}/`} />
        <meta property="og:image" content={`${ORIGIN}/TUT.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="TradesUTrust - Find trusted tradespeople across the UK" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TradesUTrust - Find Trusted Tradespeople Near You" />
        <meta name="twitter:description" content="Post a job free, compare quotes, and hire with confidence." />
        <meta name="twitter:image" content={`${ORIGIN}/TUT.png`} />

        <meta name="application-name" content="TradesUTrust" />
        <meta name="apple-mobile-web-app-title" content="TradesUTrust" />

        <script
          type="application/ld+json"
        >{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'TradesUTrust',
          alternateName: ['Trades U Trust'],
          url: `${ORIGIN}/`,
          logo: `${ORIGIN}/TUTU.png`,
          sameAs: [
            'https://www.facebook.com/people/Trades-U-Trust/61574676070793/',
            'https://www.instagram.com/tradesutrust/',
            'https://x.com/tradesutrust',
            'https://www.youtube.com/@tradesutrust',
            'https://www.linkedin.com/company/tradesutrust/',
            'https://www.tiktok.com/@tradesutrust',
          ],
        })}</script>

        <script
          type="application/ld+json"
        >{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'TradesUTrust',
          alternateName: ['Trades U Trust'],
          url: `${ORIGIN}/`,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${ORIGIN}/find-tradesman?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        })}</script>
      </Helmet>

      <TradeBanner />
      <Hero formState={formState} setFormState={setFormState} />
      <Banner />
      <HowItWorks />

      <DeferredBlock>
        {showInstagram ? (
          <Suspense fallback={null}>
            <InstagramFeedSection />
          </Suspense>
        ) : null}
      </DeferredBlock>

      <TradesCara />

      <SeoContentSection>
        <SeoHeading>Find Reliable UK Tradespeople Faster with TradesUTrust</SeoHeading>
        <SeoParagraph>
          TradesUTrust helps homeowners connect with trusted local professionals for home repairs,
          renovations, emergency callouts, and planned maintenance. Instead of messaging dozens of
          providers and waiting for responses, you can post your job once and hear from relevant,
          verified tradespeople in your area. Whether you need an electrician in Sheffield,
          a plumber in Liverpool, a carpenter for custom joinery, or a qualified roofer to assess
          storm damage, the platform is designed to make the hiring process simple, transparent, and
          safe.
        </SeoParagraph>

        <SeoSubHeading>How We Help You Hire With Confidence</SeoSubHeading>
        <SeoParagraph>
          Every job post includes your key requirements, timeline, and location so the right
          tradespeople can respond quickly. You can compare interest from professionals based on
          profile quality, relevant trade category, and communication speed before moving forward.
          This gives you more control over cost, timelines, and quality from the first interaction.
          The goal is not just to help you find someone available, but to help you find the right
          person for the job.
        </SeoParagraph>

        <SeoSubHeading>Popular Services on TradesUTrust</SeoSubHeading>
        <SeoList>
          <li>Electrical work, EICR-related jobs, and safety upgrades</li>
          <li>Plumbing repairs, leak detection, and bathroom installations</li>
          <li>Carpentry and joinery for internal and external projects</li>
          <li>Painting, decorating, plastering, and interior finishing</li>
          <li>General building jobs, repairs, and property improvements</li>
          <li>Roofing and exterior maintenance services</li>
        </SeoList>

        <SeoSubHeading>Built for Homeowners and Tradespeople</SeoSubHeading>
        <SeoParagraph>
          Homeowners get a faster route to reliable quotes and better visibility on who is responding
          to each request. Tradespeople get better quality local leads and a clear way to find jobs
          that fit their expertise. This balance helps both sides move from enquiry to completion
          with fewer delays and less back-and-forth.
        </SeoParagraph>

        <SeoSubHeading>Covering Trades Across the UK</SeoSubHeading>
        <SeoParagraph>
          TradesUTrust supports local hiring across major towns and cities in the United Kingdom.
          If your project is urgent, you can prioritise immediate responses. If your project is
          planned, you can compare options and choose based on experience and fit. From smaller
          domestic repairs to larger home improvement projects, our mission is to help you discover
          dependable professionals near you, get quotes quickly, and move your project forward with
          confidence.
        </SeoParagraph>

        <SeoSubHeading>Why Homeowners Use TradesUTrust</SeoSubHeading>
        <SeoParagraph>
          Home projects can become stressful when you do not know who to trust, what a fair price
          looks like, or how quickly work can start. TradesUTrust is built to remove that uncertainty.
          You can describe your job clearly, receive responses from suitable professionals, and choose
          the person who best matches your priorities, whether that is speed, specialist experience,
          communication style, or overall value. This workflow helps reduce delays, lowers the risk
          of hiring the wrong contractor, and keeps your project moving from first enquiry to finished
          result.
        </SeoParagraph>

        <SeoSubHeading>Support for Common Home and Property Needs</SeoSubHeading>
        <SeoParagraph>
          Many users come to TradesUTrust for recurring household needs such as boiler issues, faulty
          sockets, dripping taps, damaged plaster, or planned room upgrades. Others need support for
          bigger works like extensions, full bathroom installations, garden structures, or external
          repairs after severe weather. Because the platform connects local demand with local trade
          professionals, it helps homeowners and landlords find practical support quickly while giving
          tradespeople access to nearby opportunities that match their skills. The result is a more
          reliable and efficient way to get property work done across the UK.
        </SeoParagraph>
      </SeoContentSection>

      <Title>FAQs</Title>
      <Accordian />

      <Footer />
    </PageContainer>
  );
}

export default HomePage;
